import { useDispatch, useSelector } from "react-redux"

import { clearCart, selectSubtotal, selectTax, selectDelivery, selectTotal, selectPromoDiscount, reapplyPromoCode, applyPromoCode, resetPromo, fetchCart } from "../cart/cartSlice"
import type { RootState, AppDispatch } from "../../store"

import { useState, useEffect } from "react"
import { useTheme } from "../../contexts/ThemeContext"
// Cart API is now handled by cartSlice
import { navigateTo } from "../../utils/navigation"
import { RefreshCw } from "lucide-react"

// Import the new slices
import {
  createOrder,
  selectOrderLoading,
  selectOrderError,
  selectOrderData,
  selectLastOrderId,
  clearOrderData,
  type CreateOrderRequest
} from "./createOrderSlice"
import {
  createPayment,
  selectPaymentLoading,
  selectPaymentError,
  selectClientSecret
} from "../payment/createPaymentSlice"
import {
  createOrderIntent,
  selectPaymentLoading as selectPaymentIntentLoading,
  selectPaymentError as selectPaymentIntentError,
  clearPaymentState,
  restorePaymentIntent
} from "../payment/paymentSlice"
import { selectToken, selectAuth, logout } from "../auth/authSlice"
import { selectLanguage } from "../language/languageSlice"
import { useTranslation } from "../../hooks/useTranslation"
import CurrencyDisplay from "../../components/ui/CurrencyDisplay"
import CheckoutOfferCard from "../../components/offers/CheckoutOfferCard"
import { evaluateCartOffer } from "../offers/offersSlice"
import { useCountryConfig } from "../../hooks/useCountryConfig"
import { getCurrencyTextLabel } from "../../utils/currencyUtils"
import { useUrlParams } from "../../utils/urlParams"
import { clearAllUserData, clearSuccessPageData, clearAllDataAfterPayment } from "../../utils/cartCleaner"
import brandManager from "../../services/brandManager"



export default function CheckoutScreen() {
  const dispatch = useDispatch<AppDispatch>()
  const { t, isRTL } = useTranslation()
  const { primaryColor } = useTheme()
  const countryConfig = useCountryConfig()
  const currencyLabel = getCurrencyTextLabel(
    countryConfig,
    t('currency.egyptSymbol'),
    t('common.currency')
  )
  const { getOrderInfo: getOrderInfoFromHook, getApiParams, hasRequiredParams, urlParams } = useUrlParams()

  // Cart state from Redux
  const { 
    items: cartItems, 
    apiCartData, 
    subtotal: cartSubtotal, 
    total: cartTotal, 
    isLoading: cartLoading, 
    error: cartError
  } = useSelector((state: RootState) => state.cart)

  // Clear all user data when starting a new session for security
  useEffect(() => {
    // Don't clear data here to preserve payment data for return from Paymob
    // Data will be cleared after successful payment or when explicitly needed
  }, [])

  // Redux selectors
  const token = useSelector(selectToken)
  const { phoneNumber: userPhoneNumber } = useSelector(selectAuth)
  const language = useSelector(selectLanguage)
  const orderLoading = useSelector(selectOrderLoading)
  const orderError = useSelector(selectOrderError)
  const createdOrderData = useSelector(selectOrderData)
  const lastOrderId = useSelector(selectLastOrderId)
  const paymentLoading = useSelector(selectPaymentLoading)
  const paymentError = useSelector(selectPaymentError)
  const clientSecret = useSelector(selectClientSecret)
  const { orderData: uiOrderData } = useSelector((state: RootState) => state.ui)

  // Evaluate offer when checkout opens / cart id is available
  useEffect(() => {
    if (apiCartData?.id) {
      dispatch(evaluateCartOffer())
    } else if (token) {
      dispatch(fetchCart(token))
    }
  }, [dispatch, apiCartData?.id, token])

  // New payment intent selectors
  const paymentIntentLoading = useSelector(selectPaymentIntentLoading)
  const paymentIntentError = useSelector(selectPaymentIntentError)


  // Use created order data if available, otherwise use UI order data
  const orderData = createdOrderData || uiOrderData

  // Get order info from URL parameters using the new hook
  const urlOrderInfo = getOrderInfoFromHook()

  // State to track if user is returning from payment
  const [isReturningFromPayment, setIsReturningFromPayment] = useState(false)
  const [lastPaymentData, setLastPaymentData] = useState<{
    orderId: number;
    clientSecret: string;
    paymobUrl: string;
  } | null>(null)

  // State to prevent multiple payment button clicks
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)

  const [customerInfo, setCustomerInfo] = useState({
    orderType: urlOrderInfo.orderType === 'Table' ? t('checkout.table') : t('checkout.pickup'),
    branch: urlOrderInfo.branchName || t('checkout.qadisiyaBranch'),
    phone: userPhoneNumber || "177428074", // Use user's phone number from auth state, fallback to default
    tableNumber: urlOrderInfo.tableNumber
  })

  // Promo code state
  const [promoCode, setPromoCode] = useState('')
  const [promoCodeError, setPromoCodeError] = useState('')
  const [promoCodeSuccess, setPromoCodeSuccess] = useState('')
  const [promoStatus, setPromoStatus] = useState<'idle' | 'typing' | 'submitting' | 'loading' | 'success' | 'error' | 'applied'>('idle')
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null)

  // Table availability notification state
  const [tableNotification, setTableNotification] = useState<{
    show: boolean
    message: string
    type: 'warning' | 'error'
  }>({
    show: false,
    message: '',
    type: 'warning'
  })

  // QR Code Error Popup state
  const [qrErrorPopup, setQrErrorPopup] = useState<{
    show: boolean
  }>({
    show: false
  })

  // Table Reserved Popup state
  const [tableReservedPopup, setTableReservedPopup] = useState<{
    show: boolean
    message: string
  }>({
    show: false,
    message: ''
  })

  // Check if user is returning from payment page
  useEffect(() => {
    // This logic is now handled by PaymentReturnHandler component
    // Only restore payment data for display purposes
    const paymentData = sessionStorage.getItem("paymentData")
    
    if (paymentData) {
      try {
        const parsedData = JSON.parse(paymentData)
        setLastPaymentData(parsedData)
      } catch (error) {
        sessionStorage.removeItem('paymentData')
      }
    }
  }, [])

  // Log lastPaymentData changes
  useEffect(() => {
  }, [lastPaymentData])

  // Update customerInfo when userPhoneNumber, language, or URL params change
  useEffect(() => {
    const urlInfo = getOrderInfoFromHook()
    setCustomerInfo(prev => ({
      ...prev,
      orderType: urlInfo.orderType === 'Table' ? t('checkout.table') : t('checkout.pickup'),
      branch: urlInfo.branchName || t('checkout.qadisiyaBranch'),
      phone: userPhoneNumber || prev.phone,
      tableNumber: urlInfo.tableNumber
    }))
  }, [userPhoneNumber, t, getOrderInfoFromHook])

  // Combined loading state
  const isProcessingPayment = orderLoading || paymentLoading || paymentIntentLoading || isPaymentProcessing

  // Restore payment intent from sessionStorage on mount
  useEffect(() => {
    dispatch(restorePaymentIntent())
  }, [dispatch])

  // Handle user returning from Paymob without hitting return URL
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const payState = sessionStorage.getItem('pay_state')
        const payIntent = sessionStorage.getItem('pay_intent')
        
        // If payment is pending and user returns to checkout, they likely abandoned payment
        if (payState === 'pending' && payIntent) {
          const timeSincePayment = Date.now() - new Date(JSON.parse(payIntent).timestamp).getTime()
          const timeThreshold = 20 * 1000 // 20 seconds
          
          if (timeSincePayment > timeThreshold) {
            
            // Set payment state to failed and redirect to error
            sessionStorage.setItem('pay_state', 'failed')
            sessionStorage.removeItem('pay_intent')
            
            // Redirect to error page with navigation back reason
            window.location.href = '/error?reason=navigation_back'
            return
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Handle browser back navigation from Paymob
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // Check if user is returning from Paymob via browser back
      if (event.persisted || (performance.navigation && performance.navigation.type === 2)) {
        const payState = sessionStorage.getItem('pay_state')
        const payIntent = sessionStorage.getItem('pay_intent')
        
        // If payment was pending and user used browser back, redirect to error
        if (payState === 'pending' && payIntent) {
          
          // Set payment state to failed
          sessionStorage.setItem('pay_state', 'failed')
          sessionStorage.removeItem('pay_intent')
          
          // Redirect to error page with navigation back reason
          window.location.href = '/error?reason=navigation_back'
          return
        }
      }
    }

    // Listen for page show event (handles browser back navigation)
    window.addEventListener('pageshow', handlePageShow)
    
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  // Additional check on component mount for browser back navigation
  useEffect(() => {
    // Check if user returned to checkout after being on Paymob
    const payState = sessionStorage.getItem('pay_state')
    const payIntent = sessionStorage.getItem('pay_intent')
    const lastCheckoutTime = sessionStorage.getItem('lastCheckoutTime')
    const leftCheckoutForPaymob = sessionStorage.getItem('leftCheckoutForPaymob')
    
    // Check if user came from Paymob domain (browser back navigation)
    const referrer = document.referrer
    const isFromPaymob = referrer.includes('paymob.com') || referrer.includes('ksa.paymob.com')
    
    if (payState === 'pending' && payIntent) {
      // If user came from Paymob domain, they used browser back
      if (isFromPaymob) {
        
        // Set payment state to failed
        sessionStorage.setItem('pay_state', 'failed')
        sessionStorage.removeItem('pay_intent')
        
        // Redirect to error page with navigation back reason
        window.location.href = '/error?reason=navigation_back'
        return
      }
      
      // Check time-based detection as fallback
      if (lastCheckoutTime) {
        const timeSinceLastCheckout = Date.now() - parseInt(lastCheckoutTime)
        const timeThreshold = 30 * 1000 // 30 seconds
        
        // If user returned to checkout after a reasonable time, they likely used browser back
        if (timeSinceLastCheckout > timeThreshold) {
          
          // Set payment state to failed
          sessionStorage.setItem('pay_state', 'failed')
          sessionStorage.removeItem('pay_intent')
          
          // Redirect to error page with navigation back reason
          window.location.href = '/error?reason=navigation_back'
          return
        }
      }
    }
    
    // Store current time when checkout screen is mounted
    sessionStorage.setItem('lastCheckoutTime', Date.now().toString())
  }, [])

  // Prevent back navigation after popup actions
  useEffect(() => {
    const preventBackNavigation = () => {
      // Push current state to prevent going back
      window.history.pushState(null, '', window.location.href)
    }

    const handlePopState = (event: PopStateEvent) => {
      // Prevent back navigation and stay on current page
      window.history.pushState(null, '', window.location.href)
    }

    // Add initial state to history
    preventBackNavigation()
    
    // Listen for back button attempts
    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // Handle beforeunload event to detect when user leaves checkout for Paymob
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Store timestamp when user leaves checkout for Paymob
      const payState = sessionStorage.getItem('pay_state')
      if (payState === 'pending') {
        sessionStorage.setItem('leftCheckoutForPaymob', Date.now().toString())
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Use selectors for totals calculation
  const subtotal = useSelector(selectSubtotal)
  const tax = useSelector(selectTax)
  const delivery = useSelector(selectDelivery)
  const finalTotal = useSelector(selectTotal)
  const promoDiscount = useSelector(selectPromoDiscount)
  const items = apiCartData?.items || cartItems || []
  
  // Get table price from URL parameters
  const tablePrice = urlParams.get('tablePrice') ? parseFloat(urlParams.get('tablePrice')!) : 0
  
  // Calculate total including table price
  // finalTotal is subtotal - discount (without tax - tax is calculated in backend)
  // نضيف سعر الطاولة إلى المجموع النهائي
  const totalWithTablePrice = finalTotal + tablePrice
  
  // ملاحظة: الضريبة محسوبة في الباك إند ولا تضاف للمجموع المعروض
  // الكوبون يُطبق على (subtotal + tablePrice) ثم يتم إضافة tablePrice للمجموع النهائي
  



  const handleBack = () => {
  
    navigateTo("cart")
  }

  const handleClose = () => {

    navigateTo("home")
  }

 
  const handleQrErrorGoHome = () => {
    setQrErrorPopup({ show: false })
    navigateTo("home")
  }

  const handleTableReservedGoHome = () => {
    setTableReservedPopup({ show: false, message: '' })
    window.location.replace(window.location.origin + '/')
  }

  const handleConvertToPickup = () => {
    setTableReservedPopup({ show: false, message: '' })
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.set('typeId', '2')
    currentParams.delete('tableId')
    currentParams.delete('tableName')
    currentParams.delete('tablePrice')
    const newUrl = `${window.location.origin}/?${currentParams.toString()}`
    window.location.replace(newUrl)
  }

  // Function to validate token before making API calls
  const validateToken = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`${brandManager.apiUrl}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'lang': language,
          'platform': 'qr'
        }
      })
      return response.ok
    } catch (error) {
      return false
    }
  }

  // Function to check table availability
  const checkTableAvailability = async (tableId: number, token: string): Promise<{ available: boolean; message?: string }> => {
    try {
      const response = await fetch(`${brandManager.apiUrl}/qr/tables/${tableId}/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'lang': language,
          'platform': 'qr'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        return {
          available: data.data?.available !== false,
          message: data.data?.message
        }
      } else {
        // If table status endpoint doesn't exist, assume table is available
        return { available: true }
      }
    } catch (error) {
      // If there's an error checking table status, assume table is available
      return { available: true }
    }
  }

  // Function to show table notification
  const showTableNotification = (message: string, type: 'warning' | 'error' = 'warning') => {
    setTableNotification({
      show: true,
      message,
      type
    })
    
    // Auto hide after 5 seconds
    setTimeout(() => {
      setTableNotification(prev => ({ ...prev, show: false }))
    }, 5000)
  }

  // Function to handle table change
  const handleTableChange = () => {
    setTableNotification({ show: false, message: '', type: 'warning' })
    navigateTo("home")
  }

  // Function to handle login redirect
  const handleLoginRedirect = () => {
    setTableNotification({ show: false, message: '', type: 'warning' })
    // Clear all auth data
    localStorage.removeItem('auth_token')
    localStorage.removeItem('phone_number')
    // Redirect to home page where user can login
    navigateTo("home")
  }

  // New integrated payment flow
  const handlePayment = async () => {
    // Prevent multiple clicks
    if (isPaymentProcessing) {
      return
    }

    // Set processing state
    setIsPaymentProcessing(true)

    // Clear any previous payment data to ensure fresh payment process
    sessionStorage.removeItem('paymentData')
    sessionStorage.removeItem('paymentSuccess')
    sessionStorage.removeItem('orderData')
    sessionStorage.removeItem('orderCartData')
    sessionStorage.removeItem('orderToken')
    sessionStorage.removeItem('pay_intent')
    sessionStorage.removeItem('pay_state')

    // Check if user is authenticated
    if (!token) {
      setTableReservedPopup({ 
        show: true, 
        message: t('checkout.loginRequired') || 'يرجى تسجيل الدخول أولاً'
      })
      setIsPaymentProcessing(false)
      return
    }

    // Ensure token is stored in localStorage for API interceptor
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      // If no token, try to get it from localStorage
      const storedToken = localStorage.getItem('auth_token')
      if (!storedToken) {
        setTableReservedPopup({ 
          show: true, 
          message: 'انتهت صلاحية الجلسة - يرجى تسجيل الدخول مرة أخرى'
        })
        setIsPaymentProcessing(false)
        return
      }
    }

    // Validate token before proceeding
    const tokenToUse = token || localStorage.getItem('auth_token')
    if (tokenToUse) {
      const isValidToken = await validateToken(tokenToUse)
      if (!isValidToken) {
        setTableReservedPopup({ 
          show: true, 
          message: 'انتهت صلاحية الجلسة - يرجى تسجيل الدخول مرة أخرى'
        })
        localStorage.removeItem('auth_token')
        localStorage.removeItem('phone_number')
        setIsPaymentProcessing(false)
        return
      }
    }

    // Check if cart data is available
    if (!apiCartData?.id) {
      setTableReservedPopup({ 
        show: true, 
        message: t('checkout.noCartData') || 'لا توجد بيانات سلة التسوق'
      })
      setIsPaymentProcessing(false)
      return
    }

    // Check table availability if this is a table order
    const tableId = urlParams.get('tableId') ? parseInt(urlParams.get('tableId')!) : undefined
    if (tableId && tokenToUse) {
      const tableStatus = await checkTableAvailability(tableId, tokenToUse)
      if (!tableStatus.available) {
        const message = tableStatus.message || 'الطاولة محجوزة من قبل مستخدم آخر'
        showTableNotification(`${message} - يرجى تغيير الطاولة`, 'error')
        setIsPaymentProcessing(false)
        return
      }
    }

    // Clear any previous order data to ensure fresh order creation
    dispatch(clearOrderData())
    dispatch(clearPaymentState())

    try {
      // Get API parameters for order creation
      const { branchId, typeId } = getApiParams()
      const tableId = urlParams.get('tableId') ? parseInt(urlParams.get('tableId')!) : undefined
      const tablePrice = urlParams.get('tablePrice')
      
      // Create order request payload
      const orderRequest = {
        cartId: apiCartData.id,
        platform: "qr",
        typeId: typeId,
        notes: orderData?.notes || "",
        paymentMethod: "online",
        branchId: branchId,
        phone: customerInfo.phone,
        ...(appliedPromoCode && { coboneCode: appliedPromoCode }),
        // Add table information when typeId is 3 (Table order)
        ...(typeId === 3 && tableId && {
          tableId: tableId,
          tablePrice: tablePrice ? parseFloat(tablePrice) : undefined
        })
      }

      // Log the order request for debugging
      if (appliedPromoCode) {
        const tablePriceValue = urlParams.get('tablePrice') ? parseFloat(urlParams.get('tablePrice')!) : 0
      }

      // Create order intent (creates order and payment in one call)
      const intentResult = await dispatch(createOrderIntent({
        orderData: orderRequest,
        token: token,
        lang: language
      })).unwrap()

      // Store payment state
      sessionStorage.setItem('pay_state', 'pending')
      sessionStorage.setItem('orderToken', token)
      
      // Store order data for return
      sessionStorage.setItem('orderData', JSON.stringify({
        orderId: intentResult.orderId,
        customerInfo: customerInfo,
        urlParams: {
          branchId: branchId,
          typeId: typeId,
          tableId: urlParams.get('tableId'),
          tableName: urlParams.get('tableName'),
          tablePrice: urlParams.get('tablePrice'),
          branchName: urlParams.get('branchName')
        },
        timestamp: new Date().toISOString()
      }))
      
      // Store cart data for order display
      sessionStorage.setItem('orderCartData', JSON.stringify({
        items: apiCartData.items,
        total_price: apiCartData.total_price,
        tax_value: 0,
        price: apiCartData.total_price
      }))

      // Build Paymob URL
      const paymobPublicKey = process.env.REACT_APP_PAYMOB_PUBLIC_KEY
      if (!paymobPublicKey) {
        throw new Error('Paymob public key not found in environment variables')
      }

      // Extract the actual client_secret from the enhanced payment token
      let clientSecret = intentResult.paymentToken
      try {
        const enhancedToken = JSON.parse(intentResult.paymentToken)
        clientSecret = enhancedToken.token
      } catch (e) {
        // If parsing fails, use the token as is (fallback)
      }

      // Build Paymob URL with return URL and order_id
      const returnUrl = `${window.location.origin}/payment/return`
      const paymobUrl = `https://ksa.paymob.com/unifiedcheckout/?publicKey=${encodeURIComponent(paymobPublicKey)}&clientSecret=${clientSecret}&return_url=${encodeURIComponent(returnUrl)}`

      // Store the order_id in sessionStorage for Paymob return handling
      sessionStorage.setItem('paymob_order_id', intentResult.orderId)

      // Redirect to Paymob
      window.location.href = paymobUrl
      
    } catch (error: any) {
      // Clear any stored payment data on error
      sessionStorage.removeItem('paymentData')
      sessionStorage.removeItem('orderData')
      sessionStorage.removeItem('orderCartData')
      sessionStorage.removeItem('urlParams')
      sessionStorage.removeItem('paymobUrl')
      sessionStorage.removeItem('paymentSuccess')
      sessionStorage.removeItem('orderToken')
      sessionStorage.removeItem('pay_intent')
      sessionStorage.removeItem('pay_state')
      
      // Handle specific error types
      let errorMessage = error.message || t('checkout.paymentError') || 'Payment failed'
      
      if (error.message?.includes('انتهت صلاحية الجلسة') || error.message?.includes('has_no_open_session')) {
        errorMessage = 'انتهت صلاحية الجلسة - يرجى تسجيل الدخول مرة أخرى'
        // Show session expired notification
        showTableNotification(errorMessage, 'error')
        // Clear auth data and redirect to login
        localStorage.removeItem('auth_token')
        localStorage.removeItem('phone_number')
        return
      } else if (error.message?.includes('session_not_belong_to_user')) {
        // Show QR error popup instead of table notification
        setQrErrorPopup({ show: true })
        return
      } else if (error.message?.includes('الطاولة محجوزة') || error.message?.includes('table')) {
        errorMessage = 'الطاولة محجوزة من قبل مستخدم آخر - يرجى تغيير الطاولة'
        // Show table notification instead of alert
        showTableNotification(errorMessage, 'error')
        return
      } else if (error.message?.includes('403') || error.message?.includes('forbidden') || error.message?.includes('session')) {
        errorMessage = 'انتهت صلاحية الجلسة - يرجى تسجيل الدخول مرة أخرى'
        // Clear auth data and redirect to login
        localStorage.removeItem('auth_token')
        localStorage.removeItem('phone_number')
        // You might want to dispatch logout action here
      }
      
      // Show error to user with popup instead of alert
      setTableReservedPopup({ 
        show: true, 
        message: errorMessage
      })
    } finally {
      // Reset processing state
      setIsPaymentProcessing(false)
    }
  }

  // Function to clear all data after successful payment
  const clearAllDataAfterPaymentLocal = () => {
    try {
      // Clear cart from Redux store
      dispatch(clearCart())
      
      // Clear auth token
      dispatch(logout())
      
      // Clear all stored data using the utility function
      clearAllDataAfterPayment()
      
      // Clear order and payment data from Redux
      dispatch(clearOrderData())
      dispatch(clearPaymentState())
      
      // Clear promo code
      dispatch(resetPromo())
      
      console.log('✅ All data cleared after payment')
    } catch (error) {
      console.error('❌ Error clearing data after payment:', error)
    }
  }

  // Function to retry payment (go back to Paymob)
  const handleRetryPayment = () => {
    // Clear all data after payment
    clearAllDataAfterPaymentLocal()
    
    if (lastPaymentData?.paymobUrl) {
      window.open(lastPaymentData.paymobUrl, '_self')
    } else {
      // If no payment data, try to create new payment
      handlePayment()
    }
  }

  // Helper function to format numbers in English
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num)
  }

  // Input validation function to prevent script injection and suspicious characters
  const validatePromoCodeInput = (input: string): { isValid: boolean; error?: string } => {
    // Remove whitespace
    const trimmedInput = input.trim()
    
    // Check for empty input
    if (!trimmedInput) {
      return { isValid: false, error: t('cart.promoCodeInvalid') }
    }
    
    // Check for suspicious patterns that could be script injection
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /eval\s*\(/i,
      /expression\s*\(/i,
      /vbscript:/i,
      /data:/i,
      /&#x?[0-9a-f]+;/i,
      /%[0-9a-f]{2}/i,
      /[<>'"&]/,
      /[(){}[\]]/,
      /[;|&$`]/,
      /\s{3,}/, // Multiple consecutive spaces
      /[^\w\s-]/ // Only allow alphanumeric, spaces, and hyphens
    ]
    
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(trimmedInput)) {
        return { isValid: false, error: t('cart.promoCodeInvalid') }
      }
    }
    
    // Check length (reasonable limit for promo codes)
    if (trimmedInput.length > 50) {
      return { isValid: false, error: t('cart.promoCodeInvalid') }
    }
    
    return { isValid: true }
  }

  // Promo code functions
  const clearPromoInput = () => {
    setPromoCode('')
    setPromoCodeError('')
    setPromoCodeSuccess('')
    setPromoStatus('idle')
    setAppliedPromoCode(null)
    // Remove promo code from Redux state and restore original total
    dispatch(resetPromo())
  }

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return

    // Validate input before processing
    const validation = validatePromoCodeInput(promoCode)
    if (!validation.isValid) {
      setPromoStatus('error')
      setPromoCodeError(validation.error || t('cart.promoCodeInvalid'))
      return
    }

    setPromoStatus('submitting')
    setPromoCodeError('')
    setPromoCodeSuccess('')

    try {
      // Use the real API call from cartSlice
      if (!token) {
        throw new Error('Authentication required')
      }

      // إرسال إجمالي المبلغ (subtotal + tablePrice) للكوبون بدلاً من subtotal فقط
      const totalForPromo = subtotal + tablePrice
      const result = await dispatch(applyPromoCode({
        code: promoCode.trim(),
        subtotal: totalForPromo, // إرسال الإجمالي بدلاً من subtotal فقط
        token: token
      })).unwrap()
      
      // Store the actual promo code in local state for reapplication
      const actualPromoCode = promoCode.trim()
      
      // تحقق من أن الكوبون تم تطبيقه بنجاح (إما success = true أو وجود discount)
      if (result.success || (result.discount && result.discount > 0)) {
        setPromoStatus('success')
        // استخدم رسالة مترجمة
        const successMessage = `${t('cart.promoCodeSuccess')} ${t('cart.discount')} ${result.discount} ${currencyLabel}`
        setPromoCodeSuccess(successMessage)
        setAppliedPromoCode(promoCode.trim())
 } else {
        throw new Error(result.message || t('cart.promoCodeInvalid'))
      }
    } catch (error: any) {
      setPromoStatus('error')
      setPromoCodeError(error.message || t('cart.promoCodeError'))
    }
  }

  return (
    <div className="bg-gray-50 max-h-screen font-['IBM_Plex_Sans_Arabic']">
      {/* Table Notification */}
      {tableNotification.show && (
        <div className="fixed top-4 left-4 right-4 z-50 mx-auto max-w-md">
          <div className={`rounded-lg p-4 shadow-lg border-l-4 ${
            tableNotification.type === 'error' 
              ? 'bg-red-50 border-red-400 text-red-800' 
              : 'bg-yellow-50 border-yellow-400 text-yellow-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                  tableNotification.type === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                }`}>
                  <span className="text-white text-xs font-bold">
                    {tableNotification.type === 'error' ? '!' : '⚠'}
                  </span>
                </div>
                <p className="text-sm font-medium">{tableNotification.message}</p>
              </div>
              <div className="flex items-center gap-2">
                {tableNotification.message.includes('انتهت صلاحية الجلسة') ? (
                  <button
                    onClick={handleLoginRedirect}
                    className="px-3 py-1 bg-red-500 text-white text-xs rounded-md hover:bg-red-600 transition-colors"
                  >
                    تسجيل الدخول
                  </button>
                ) : (
                  <button
                    onClick={handleTableChange}
                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600 transition-colors"
                  >
                    تغيير الطاولة
                  </button>
                )}
                <button
                  onClick={() => setTableNotification({ show: false, message: '', type: 'warning' })}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Error Popup */}
      {qrErrorPopup.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="text-center">
              {/* Warning Icon */}
              <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-['IBM_Plex_Sans_Arabic']">
                {t('popup.warning') || 'تحذير'}
              </h3>
              
              {/* Message */}
              <p className="text-gray-600 mb-6 font-['IBM_Plex_Sans_Arabic'] leading-relaxed">
                {t('popup.wrongQrMessage') || 'تم مسح رمز QR خاطئ. يجب عمل مسح مرة أخرى للرمز الصحيح'}
              </p>
              
              {/* Button */}
              <button
                onClick={handleQrErrorGoHome}
                className="w-full py-3 px-4 text-white rounded-xl font-bold transition-all duration-200 font-['IBM_Plex_Sans_Arabic'] hover:shadow-md"
                style={{ backgroundColor: primaryColor }}
              >
                {t('popup.scanAgain') || 'مسح الرمز مرة أخرى'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Reserved Error Popup */}
      {tableReservedPopup.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="text-center">
              {/* Warning Icon */}
              <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: primaryColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-['IBM_Plex_Sans_Arabic']">
                {urlParams.get('tableId') ? t('popup.tableOccupied') || 'طاولة مشغولة' : t('popup.notice') || 'تنبيه'}
              </h3>
              
              {/* Message */}
              <p className="text-gray-600 mb-6 font-['IBM_Plex_Sans_Arabic'] leading-relaxed">
                {urlParams.get('tableId') ? 
                  t('popup.tableOccupiedMessage') || 'هذه الطاولة مشغولة حالياً. يمكنك مسح رمز QR مرة أخرى أو تحويل طلبك للاستلام من الفرع' : 
                  tableReservedPopup.message
                }
              </p>
              
              {/* Buttons */}
              {urlParams.get('tableId') ? (
                <div className="space-y-3">
                  {/* Pickup Button */}
                  <button
                    onClick={handleConvertToPickup}
                    className="w-full py-3 px-4 text-white rounded-xl font-bold transition-all duration-200 font-['IBM_Plex_Sans_Arabic'] hover:shadow-md"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {t('popup.orderForPickup') || 'طلب للاستلام من الفرع'}
                  </button>
                  
                  {/* Scan Again Button */}
                  <button
                    onClick={handleTableReservedGoHome}
                    className="w-full py-3 px-4 bg-gray-500 text-white rounded-xl font-bold transition-all duration-200 font-['IBM_Plex_Sans_Arabic'] hover:bg-gray-600 hover:shadow-md"
                  >
                    {t('popup.scanAgain') || 'مسح الرمز مرة أخرى'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleTableReservedGoHome}
                  className="w-full py-3 px-4 text-white rounded-xl font-bold transition-all duration-200 font-['IBM_Plex_Sans_Arabic'] hover:shadow-md"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t('popup.scanAgain') || 'مسح الرمز مرة أخرى'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="bg-white">
          <div className="container mx-auto px-4 py-3 flex items-center">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-700 font-['IBM_Plex_Sans_Arabic']"
            >
              <div className="border border-gray-200 rounded-xl p-1.5 text-black">
                {isRTL ? (
                  <img src="/back.svg" alt={t('common.back')} className="h-6 w-6" />
                ) : (
                  <img src="/back_lift.svg" alt={t('common.back')} className="h-6 w-6" />
                )}
              </div>
              <span className={`font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'mr-2' : 'ml-2'}`}>{t('checkout.payment')}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-3 py-3  bg-white max-h-screen mx-auto space-y-3 w-full font-['IBM_Plex_Sans_Arabic']">
        {/* Order Items */}
        <div className="bg-gray-100 rounded-xl   p-0.5">
          

          {/* Order Info */}
          <div className="text-sm text-gray-600 space-y-3 py-3 px-3 bg-white rounded-xl font-['IBM_Plex_Sans_Arabic']">
            <div className="flex justify-between border-b border-gray-200 pb-3">
              <span className="text-gray-700 font-medium font-['IBM_Plex_Sans_Arabic']">{t('checkout.orderType')}</span>
              <span className="text-gray-800 font-['IBM_Plex_Sans_Arabic']">{customerInfo.orderType}</span>
            </div>
            <div className="flex justify-between border-b border-gray-200 py-1 pb-3">
              <span className="text-gray-700 font-medium font-['IBM_Plex_Sans_Arabic']">{t('checkout.branch')}</span>
              <span className="text-gray-800 font-['IBM_Plex_Sans_Arabic']">{customerInfo.branch}</span>
            </div>
            {customerInfo.tableNumber && (
              <div className="flex justify-between border-b border-gray-200 py-1 pb-3">
                <span className="text-gray-700 font-medium font-['IBM_Plex_Sans_Arabic']">{t('checkout.tableNumber')}</span>
                <span className="text-gray-800 font-['IBM_Plex_Sans_Arabic']">{customerInfo.tableNumber}</span>
              </div>
            )}
            <div className="flex justify-between  py-1 pb-3">
              <span className="text-gray-700 font-medium font-['IBM_Plex_Sans_Arabic']">{t('checkout.phoneNumber')}</span>
              <span className="text-gray-800 font-['IBM_Plex_Sans_Arabic']" dir="ltr">
                {customerInfo.phone}
              </span>
            </div>
            {createdOrderData && (
              <>
                <div className="flex justify-between border-b border-gray-200 py-1 pb-3">
                  <span className="text-gray-700 font-medium font-['IBM_Plex_Sans_Arabic']">{t('checkout.orderNumber')}</span>
                  <span className="text-gray-800 font-['IBM_Plex_Sans_Arabic']">#{createdOrderData.id}</span>
                </div>
                <div className="flex justify-between ">
                  <span className="text-gray-700 font-medium font-['IBM_Plex_Sans_Arabic']">{t('checkout.paymentStatus')}</span>
                  <span className="text-gray-800 font-['IBM_Plex_Sans_Arabic']">
                    {createdOrderData.payment_status === 'waiting_payment' ? t('checkout.waitingPayment') : createdOrderData.payment_status}
                  </span>
                </div>
              </>
            )}
            
       
            
          </div>
        </div>

        <div className="bg-white rounded-lg w-full ">
  {promoStatus === 'applied' && appliedPromoCode ? (
    // حالة الكوبون المطبق
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
            {appliedPromoCode}
          </span>
          <span className="text-xs text-green-600 font-medium">✓ {t('cart.applied')}</span>
        </div>
      </div>

      {promoDiscount > 0 && (
        <div className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-lg border border-green-200">
          <span className="text-sm text-green-700 font-medium">{t('cart.discount')}</span>
          <span className="text-sm text-green-700 font-bold">-{promoDiscount} {currencyLabel}</span>
        </div>
      )}
    </div>
  ) : (
    // حالة إدخال الكوبون
    <div className="w-full">
      <form
        onSubmit={(e) => { e.preventDefault(); handleApplyPromoCode(); }}
        className={[
          "w-full transition-all duration-500 ease-in-out",
          // نخلي عمودين طول ما الزر ظاهر (idle/typing/submitting/loading)
          (promoStatus === 'idle' || promoStatus === 'typing' || promoStatus === 'submitting' || promoStatus === 'loading')
            ? "grid grid-cols-[1fr_auto] gap-2"
            : "grid grid-cols-[1fr] gap-0"
        ].join(' ')}
      >
        <div className="relative">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => {
              const inputValue = e.target.value
              
              // Real-time validation to prevent suspicious input
              const validation = validatePromoCodeInput(inputValue)
              
              // Only update if input is valid or if it's being cleared
              if (validation.isValid || inputValue === '') {
                setPromoCode(inputValue)
                setPromoCodeError('')
                setPromoCodeSuccess('')
                if (promoStatus !== 'loading' && promoStatus !== 'submitting') {
                  setPromoStatus(inputValue.trim() ? 'typing' : 'idle')
                }
              } else {
                // Show error for invalid input without updating the field
                setPromoCodeError(validation.error || t('cart.promoCodeInvalid'))
                setPromoStatus('error')
              }
            }}
            placeholder={
              promoStatus === 'success'
                ? t('cart.promoCodeSuccess')
                : (t('cart.promoCodePlaceholder') || 'كود الخصم...')
            }
            className={[
              "w-full h-10 pr-3 rounded-xl text-sm text-black placeholder-gray-500 focus:outline-none transition-all duration-500 ease-in-out bg-white truncate",
              promoStatus === 'error'
                ? "border-2 border-red-300 focus:border-red-400 shadow-sm pl-10"
                : (promoStatus === 'success' || promoStatus === 'applied')
                ? "border-2 border-green-300 focus:border-green-400 bg-green-50 shadow-sm pl-3"
                : (promoStatus === 'submitting' || promoStatus === 'loading')
                ? "border border-gray-300 pl-10"
                : "border border-gray-200 focus:border-gray-300 focus:shadow-sm pl-3"
            ].join(' ')}
            dir={isRTL ? "rtl" : "ltr"}
            aria-label="Promo code"
            readOnly={promoStatus === 'success' || promoStatus === 'applied'}
          />

          {/* × داخل الحقل تظهر بعد الإرسال (submitting/loading/success/error) */}
          {(promoStatus === 'submitting' || promoStatus === 'loading' || promoStatus === 'success' || promoStatus === 'error') && (
            <button
              type="button"
              onClick={clearPromoInput}
              className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition ${isRTL ? 'left-3' : 'right-3'}`}
              title={t('cart.clear') || 'مسح'}
              aria-label="Clear promo code"
            >
              <span className="text-sm font-bold leading-none">×</span>
            </button>
          )}
        </div>

        {/* زر يسار: يبدّل حسب الحالة */}
        {(promoStatus === 'idle' || promoStatus === 'typing') && (
          <button
            type="submit"
            disabled={!promoCode.trim()}
            className={[
              "w-20 h-10 px-3 rounded-xl text-sm font-medium flex items-center justify-center transition-all duration-300 shadow-sm text-white",
              !promoCode.trim()
                ? "bg-gray-300 cursor-not-allowed"
                : "hover:shadow-md transform hover:scale-105"
            ].join(' ')}
            style={!promoCode.trim() ? {} : { backgroundColor: primaryColor }}
          >
            {t('cart.apply') || 'تطبيق'}
          </button>
        )}

        {(promoStatus === 'submitting' || promoStatus === 'loading') && (
          <button
            type="button"
            disabled
            className="w-20 h-10 px-3 rounded-xl text-white flex items-center justify-center transition-all duration-300 shadow-sm"
            style={{ backgroundColor: primaryColor + 'E6' }}
            title={t('cart.applying') || 'جاري التطبيق...'}
            aria-busy
          >
            <RefreshCw className="w-4 h-4 animate-spin" />
          </button>
        )}
      </form> 

      {/* رسالة النجاح */}
      {promoStatus === 'success' && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">✓</span>
            </div>
            <span className="text-sm text-green-700 font-medium">{promoCodeSuccess}</span>
          </div>
        </div>
      )}

      {/* رسالة الخطأ */}
      {promoStatus === 'error' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">✕</span>
            </div>
            <span className="text-sm text-red-700 font-medium">{promoCodeError}</span>
          </div>
        </div>
      )}
    </div>
  )}
</div>
        {/* Payment Method */}
        <div className="flex justify-between items-center border border-gray-200 rounded-2xl px-4 py-3 bg-white font-['IBM_Plex_Sans_Arabic']">
          {/* Right side: Title + Radio */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 rounded-full flex items-center justify-center" style={{ borderColor: primaryColor }}>
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }}></div>
            </div>
            <span className="text-sm font-medium text-gray-900 font-['IBM_Plex_Sans_Arabic']" style={{color:primaryColor}}>{t('checkout.electronicPayment')}</span>
          </div>

          {/* Left side: Payment logos */}
          <div className="flex items-center gap-3">
            <img src="/made.svg" alt="Mada" className="h-3" />
            <img src="/apple-pay.svg" alt="Apple Pay" className="h-3" />
            <img src="/mastercard.svg" alt="MasterCard" className="h-3" />
            <img src="/visa.svg" alt="Visa" className="h-3" />
          </div>
        </div>

        {/* Automatic offer — Mobile-style full card */}
        <CheckoutOfferCard />



        
      </div>

      {/* Summary & Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white mx-auto w-full px-5 py-5 font-['IBM_Plex_Sans_Arabic']">
        <div className="mx-auto space-y-2">
          {/* Subtotal */}
          <div className="flex justify-between text-sm text-gray-600">
            <span className="font-['IBM_Plex_Sans_Arabic'] font-normal">{t('checkout.subtotal')}</span>
            <div className="flex items-center gap-1.5" dir="ltr">
              <CurrencyDisplay src="/SAR_gray.svg" className="w-5 h-5" />
              <span className="font-['IBM_Plex_Sans_Arabic'] font-normal text-gray-600">{formatNumber(subtotal)}</span>
            </div>
          </div>

          {/* Tax */}
          <div className="flex justify-between text-sm text-gray-600">
            <span className="font-['IBM_Plex_Sans_Arabic']">{t('checkout.tax')}</span>
            <div className="flex items-center gap-1.5" dir="ltr">
              <CurrencyDisplay src="/SAR_gray.svg" className="w-5 h-5" />
              <span className="font-['IBM_Plex_Sans_Arabic'] text-gray-600">{formatNumber(tax)}</span>
            </div>
          </div>

          {/* Delivery */}
          {delivery > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span className="font-['IBM_Plex_Sans_Arabic']">{t('cart.delivery')}</span>
              <div className="flex items-center gap-1.5" dir="ltr">
                <CurrencyDisplay src="/SAR_gray.svg" className="w-5 h-5" />
                <span className="font-['IBM_Plex_Sans_Arabic'] text-gray-600">{formatNumber(delivery)}</span>
              </div>
            </div>
          )}
            {/* Table Price */}
            {tablePrice > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span className="font-['IBM_Plex_Sans_Arabic']">{t('checkout.tablePrice') || 'سعر الطاولة'}</span>
              <div className="flex items-center gap-1.5" dir="ltr">
                <CurrencyDisplay src="/SAR_gray.svg" className="w-5 h-5" />
                <span className="font-['IBM_Plex_Sans_Arabic'] text-gray-600">{formatNumber(tablePrice)}</span>
              </div>
            </div>
          )}

          {/* Discount */}
          {promoDiscount > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span className="font-['IBM_Plex_Sans_Arabic']">{t('cart.discount')}</span>
              <div className="flex items-center gap-1.5" dir="ltr">
                <CurrencyDisplay src="/SAR_gray.svg" className="w-5 h-5" />
                <span className="font-['IBM_Plex_Sans_Arabic'] text-gray-600">-{formatNumber(promoDiscount)}</span>
              </div>
            </div>
          )}

        

          {/* Total */}
          <div className="flex justify-between text-sm text-black font-bold pt-2">
            <span className="font-['IBM_Plex_Sans_Arabic']">{t('checkout.total')}</span>
            <div className="flex items-center gap-1.5" dir="ltr">
              <CurrencyDisplay className="w-4 h-5" iconStyle={{ color: primaryColor }} />
              <span className="font-bold text-lg font-['IBM_Plex_Sans_Arabic']" style={{ color: primaryColor }}>{formatNumber(totalWithTablePrice)}</span>
            </div>
          </div>

          <button
            onClick={handlePayment}
            disabled={isProcessingPayment}
            className="w-full py-2.5 text-white text-center rounded-xl font-bold transition font-['IBM_Plex_Sans_Arabic'] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{
              backgroundColor: isProcessingPayment ? '#9CA3AF' : primaryColor,
              borderColor: isProcessingPayment ? '#9CA3AF' : primaryColor,
              border: '1px solid'
            }}
          >
            {isProcessingPayment ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{t('checkout.processingOrder') || 'جاري التحميل...'}</span>
              </>
            ) : (
              t('checkout.completeOrder')
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
